import React, { useCallback, useMemo } from 'react';
import { SearchUser } from '~/types';
import classes from './style.module.scss';
import { useStoreActions, useStoreState } from '~/store';
import Icon from '~/app/common/Icon';
import useTranslation from '~/app/hooks/useTranslation';
import InfiniteScroll from 'react-infinite-scroll-component';
import { getChatStoreKey, MessageType } from '~/app/data/statuses/chat';

export interface SelectedUserComponentProps {
  searchText: string
  type: MessageType
}

export type SelectedUserComponent = React.FC<SelectedUserComponentProps>;

const SelectedUserList: SelectedUserComponent = ({ searchText, type }) => {

  const basePath = getChatStoreKey(type);
  const chatSlice = useStoreState(state => state[basePath]);
  const isReadOnlyChat = chatSlice?.isReadOnlyChat ?? false;
  const chatEntityId = chatSlice?.data?.id;
  const userId = useStoreState(state => state.user.data.id);
  const searchUsers = useStoreState(state => state.user.searchUsers);
  const searchUsersTotal = useStoreState(state => state.user.searchUsersTotal ?? 0);
  const searchUserAction = useStoreActions(actions => actions.user.fetchSearchUser)
  const tempChatUsers = useMemo(() => chatSlice?.tempChatUsers ?? [], [chatSlice?.tempChatUsers]);
  const setTempChatUsersAction = useStoreActions(actions => actions[basePath].setTempChatUsers);

  const { t } = useTranslation('user.addUser');

  const displayedUsers = useMemo(() => {
    if (searchText === '') {
      return tempChatUsers;
    }

    const tempMap = new Map(tempChatUsers.filter(t => t.id !== undefined).map(user => [user?.id?.toString().trim(), user]));

    return searchUsers
      .map(user => {
        const id = user?.id?.toString().trim();
        const existing = tempMap.get(id);
  
        if (isReadOnlyChat) {
          return existing;
        }
  
        return existing || user;
      })
      .filter((u): u is SearchUser => u !== undefined);
  }, [searchText, tempChatUsers, searchUsers, isReadOnlyChat]);

  const handleAdd = useCallback((user: SearchUser) => {
    const updatedUsers = [...tempChatUsers];
    const index = updatedUsers.findIndex(u => u.id.toString().trim() === user.id);
    if (index !== -1) {
      updatedUsers[index].added = true;
      updatedUsers[index].deleted = false;
    } else {
      updatedUsers.unshift({ ...user, added: true, new: true });
    }
    setTempChatUsersAction(updatedUsers);
  }, [tempChatUsers, setTempChatUsersAction]);

  const handleDelete = useCallback((id: string) => {
    const updatedUsers = [...tempChatUsers];
    const index = updatedUsers.findIndex((tm) => tm.id === id);
    if (index !== -1) {
      if (updatedUsers[index].new) {
        updatedUsers.splice(index, 1);
      } else {
        updatedUsers[index] = {
          ...updatedUsers[index],
          added: false,
          deleted: true,
        };
      }
      setTempChatUsersAction(updatedUsers);
    }
  },[tempChatUsers, setTempChatUsersAction]);

  const fetchNext = useCallback(() => {
    searchUserAction(
      type === MessageType.PROJECT
        ? { searchText, offset: searchUsers.length, machineId: String(chatEntityId ?? '') }
        : { searchText, offset: searchUsers.length },
    );
  }, [searchText, searchUserAction, searchUsers.length, type, chatEntityId]);

  const showDeleteButton = useCallback((id: string) => {
    return parseInt(userId, 10) !== parseInt(id, 10);
  }, [userId]);

  return (
    <div className={classes['userList']} id="search-scroll-wrapper">
      {displayedUsers.length === 0 ? (
        <p>{t('noUsersFound')}</p>
      ) : (
        <InfiniteScroll
          dataLength={searchUsers.length}
          next={fetchNext}
          hasMore={searchUsers.length < searchUsersTotal}
          loader={<div></div>}
          scrollableTarget="search-scroll-wrapper"
        >
          {displayedUsers.map((user) => (
            <div className={`${classes['userItem']} ${user.new ? classes['new'] : ''} ${user.deleted ? classes['deleted'] : ''}`} key={user.id}>
              <div className={classes['row']}>
                <div className={classes['column']}>
                  <div className={classes['userListItem']}>
                    <img src={user.avatar} alt={user.name} />
                    {user.name}
                  </div>
                </div>
              </div>
              {!isReadOnlyChat && (
                <div className={classes['column']}>
                  {user.added ? (
                      showDeleteButton(user.id) && (
                        <button className={`${classes['deleteButton']} ${user.new ? classes['new'] : ''}`} onClick={() => handleDelete(user.id)}>
                          <Icon name="delete" />
                      </button>
                    )
                  ) : (
                    <button
                      className={`${classes['addButton']} ${user.deleted ? classes['deleted'] : ''}`}
                      onClick={() => handleAdd(user)}
                    >
                      <Icon name="add" />
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </InfiniteScroll>
      )}
    </div>
  );
};

export default SelectedUserList;
